import { inject, Injectable } from '@angular/core';
import { isValid } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { IAFilterValues } from '../types';
import { from, map, Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { AppConfigService } from '@sphere/platform';

export const CHATBOT_CONFIG_KEY = 'spherechatbot-configuration';

export type ChatbotMode = 'market_scan' | 'intelligence' | 'intelligence_basic';
export const CHATBOT_MODE_MARKET_SCAN = 'market_scan';
export const CHATBOT_MODE_INTELLIGENCE = 'intelligence';
export const CHATBOT_MODE_INTELLIGENCE_BASIC = 'intelligence_basic';

export const IA_MARKET_SCAN_TITLE = 'InsiteEdge';
export const IA_INTELLIGENCE_BASIC_TITLE = 'Enverus Intel Basic';
export const IA_INTELLIGENCE_TITLE = 'Enverus Intelligence';

export const IA_MARKET_SCAN_ICON = 'manage_search';
export const IA_INTELLIGENCE_BASIC_ICON = 'search_insights';
export const IA_INTELLIGENCE_ICON = 'travel_explore';

export type AiFilterContextValues = AiFilterContextValuesIntelligence | AiFilterContextValuesMarketScan;

export type AiFilterContextValuesCommon = {
    acls: {
        values: string[];
    };
};
export type AiFilterContextValuesIntelligence = {
    publishedDate?: {
        startDate: string | Date;
        endDate: string | Date;
    };
} & AiFilterContextValuesCommon;
export type AiFilterContextValuesMarketScan = {
    published_date?: {
        start_date: string | Date;
        end_date: string | Date;
    };
} & AiFilterContextValuesCommon;

export enum IASourceCategory {
    Marketscan = 'marketscan',
    Intelligence = 'intelligence'
}

export enum IAIntelligenceSourceCode {
    Basic = 'basic',
    Complete = 'complete'
}

@Injectable({
    providedIn: 'root'
})
export class ChatbotService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(AppConfigService);

    generateContext(filtersData: IAFilterValues, mode: ChatbotMode): AiFilterContextValues {
        const { startDate, endDate } = filtersData.dateRange;
        let propertyName: string;
        let propertyValue: any = {};
        switch (mode) {
            case 'market_scan':
                propertyName = 'published_date';
                propertyValue = this.formatDateRangeMarketScan(startDate, endDate);
                break;
            case 'intelligence':
            case 'intelligence_basic':
            default:
                propertyName = 'publishedDate';
                propertyValue = this.formatDateRangeIntelligence(startDate, endDate);
        }
        return {
            acls: {
                values: [...new Set(filtersData.aclValues)]
            },
            [propertyName]: propertyValue
        };
    }

    private formatDateRangeMarketScan(startDate: Date | string, endDate: Date | string): { start_date: string | Date; end_date: string | Date } {
        return {
            start_date: isValid(startDate) ? fromZonedTime(startDate as Date, 'UTC').toISOString() : startDate,
            end_date: isValid(endDate) ? fromZonedTime(endDate as Date, 'UTC').toISOString() : endDate
        };
    }

    private formatDateRangeIntelligence(startDate: Date | string, endDate: Date | string): { startDate: string | Date; endDate: string | Date } {
        return {
            startDate: isValid(startDate) ? fromZonedTime(startDate as Date, 'UTC').toISOString() : startDate,
            endDate: isValid(endDate) ? fromZonedTime(endDate as Date, 'UTC').toISOString() : endDate
        };
    }

    getPdfById(pdfId: string): Observable<HttpResponse<Blob>> {
        const [id, query] = pdfId.split('?');
        const params = new URLSearchParams(query);
        const citationId = id;
        const authority = params.get('authority');
        const newsSource = params.get('news_source');

        const baseUrl = this.config.apiGatewayNexus2Url;
        const url = `${baseUrl}/api/instant-analyst/market-scan/citation/${encodeURIComponent(citationId)}`;

        let queryParams = new HttpParams();
        if (authority) queryParams = queryParams.set('authority', authority);
        if (newsSource) queryParams = queryParams.set('news_source', newsSource);

        const headers = new HttpHeaders({
            Accept: 'application/pdf, application/json',
            'Content-Type': 'application/json'
        });

        return this.http.get(url, {
            headers,
            params: queryParams,
            responseType: 'blob',
            observe: 'response'
        });
    }

    public handlePdfResponse(response: HttpResponse<Blob>): Observable<string> {
        const blob = response.body;
        if (!blob) {
            throw new Error('PDF response body is empty.');
        }
        const contentType = response.headers.get('content-type') ?? '';

        if (contentType.includes('application/pdf')) {
            return of(this.setViewerUrlFromBlob(blob, 'pdf'));
        }

        const text$ = from(blob.text());

        if (contentType.includes('text/plain')) {
            // Convert text/plain to a PDF-like display
            return text$.pipe(map(text => this.setViewerUrlFromText(text)));
        }

        return text$.pipe(
            map(text => {
                if (this.isJsonString(text)) {
                    const parsed = JSON.parse(text);
                    if (parsed.pdfUrl) {
                        return parsed.pdfUrl;
                    }
                    throw new Error('PDF URL not found in JSON response.');
                }
                return this.setViewerUrlFromText(text);
            })
        );
    }

    private isJsonString(str: string): boolean {
        if (typeof str !== 'string') {
            return false;
        }
        try {
            const parsed = JSON.parse(str);
            return parsed !== null && (typeof parsed === 'object' || Array.isArray(parsed));
        } catch (e) {
            return false;
        }
    }

    private setViewerUrlFromText(textContent: string): string {
        const html = `<!DOCTYPE html>
                        <html>
                            <head>
                              <title>Text Content</title>
                                  <style>
                                    body {
                                      font-family: Arial, sans-serif;
                                      margin: 20px;
                                      line-height: 1.6;
                                    }
                                    pre {
                                      white-space: pre-wrap;
                                      background-color: #f5f5f5;
                                      padding: 15px;
                                      border-radius: 5px;
                                    }
                                  </style>
                            </head>
                            <body>
                                  <pre>${textContent}</pre>
                            </body>
                        </html>`;
        const blob = new Blob([html], { type: 'text/html' });
        return this.setViewerUrlFromBlob(blob, 'html');
    }

    private setViewerUrlFromBlob(blob: Blob, type: 'pdf' | 'html'): string {
        const blobUrl = URL.createObjectURL(blob);
        return type === 'pdf' ? `assets/pdfjs/web/viewer.html?file=${encodeURIComponent(blobUrl)}` : blobUrl;
    }
}
